import { memoize } from 'proxy-memoize';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { uniqBy } from 'es-toolkit';
import { useTranslation } from 'react-i18next';

import getApplicationStructuredDataSelector from '/business/shared/state/selectors/application/getApplicationStructuredDataSelector';
import lastRetrievedDatestampSelector from '/business/shared/state/selectors/application/lastRetrievedDatestampSelector';
import useAppContext from './useAppContext';

import getQuestionsWithAnswersSelector from '/business/shared/state/selectors/application/step1/getQuestionsWithAnswersSelector';
import type { QuestionConfigBranchLoader, QuestionConfigHookLoader, QuestionConfigInitiativeVisuals } from '/index';
import type { LazyQuestionFormModule, QuestionFormComponent, QuestionTreeItem } from '/business/shared/functions/types';
import type { RootState } from '/business/shared/state/store';

type InitiativeQuestionAnswer = {
  tonnage?: number;
};

export type InitiativeQuestion = Omit<
  QuestionTreeItem,
  'answers' | 'children' | 'groups' | 'parent' | 'questions' | 'subQuestions'
> & {
  answers?: InitiativeQuestionAnswer[];
  apiKey?: string;
  background?: QuestionConfigInitiativeVisuals['background'];
  bp_offering?: boolean;
  breadcrumbTitle?: string;
  cardBackground?: QuestionConfigInitiativeVisuals['cardBackground'];
  children?: InitiativeQuestion[];
  customInitiativeIgnoreSubQuestions?: boolean;
  custom_title?: string | null;
  depth?: number;
  description?: QuestionTreeItem['description'];
  details?: {
    title?: string;
  };
  editModeBreadcrumbTitle?: string;
  emissionCategories?: InitiativeQuestion[];
  flattened?: boolean;
  frontend_enum?: string;
  form?: LazyQuestionFormModule | QuestionFormComponent;
  groups?: InitiativeQuestion[];
  genericTonnage?: number | null;
  hasTonnage?: boolean | number | null;
  hideParentBreadcrumb?: boolean;
  id?: string;
  initiative?: QuestionTreeItem['initiative'];
  initiativeId?: string | number;
  initiativeInfo?: InitiativeInfo;
  initiativeCategoryOverride?: string;
  initiativeGroupApiKeys?: string[];
  initiativeHideWhenEmpty?: boolean;
  initiative_id?: string | number;
  isSelectorPage?: boolean;
  logic_key?: string | null;
  numOfChildren?: number;
  parent?: InitiativeQuestion | null;
  path?: QuestionTreeItem['path'];
  question_type_key?: string;
  questions?: InitiativeQuestion[];
  removeBreadcrumbLink?: boolean;
  reportingPeriodYear?: number;
  scope?: string;
  scopeCategory?: string;
  splitQuestions?: boolean;
  subQuestions?: InitiativeQuestion[];
  title?: string;
  tonnageReducedBy?: number;
  type?: string;
  useHookLoader?: QuestionConfigHookLoader;
  useQuestionBranchCustomLoader?: QuestionConfigBranchLoader;
  useQuestionBranchSupplierLoader?: QuestionConfigBranchLoader;
};

export type Initiative = {
  answer_enum?: string | null;
  bp_offering?: boolean;
  custom_title?: string | null;
  frontend_enum?: string;
  initiative_id?: string | number;
  isHotspot?: boolean;
  isHotspot1?: boolean;
  isHotspot2?: boolean;
  isHotspot3?: boolean;
  logic_key?: string | null;
  question_type_key?: string;
};

export type ScopeCategoryItem = {
  hasTonnage?: boolean | number | null;
  scope?: string;
  scopeCategory?: InitiativeQuestion['scopeCategory'];
};

export type CategoryTreeItem = ScopeCategoryItem & {
  emissionCategories: InitiativeQuestion[];
};

type InitiativeQuestionCollectionKey = 'children' | 'groups' | 'questions' | 'subQuestions';

export type InitiativeInfo = {
  background: QuestionConfigInitiativeVisuals['background'] | null;
  cardBackground: QuestionConfigInitiativeVisuals['cardBackground'] | null;
  categoryTitle: string | null;
  description: QuestionConfigInitiativeVisuals['description'] | null;
  initiativeTitle: string | null;
  logicKey: string | null;
  question?: InitiativeQuestion;
  scopeTitle: string | null;
  useMask?: boolean;
};

type QuestionsWithAnswersSelector = (
  state: RootState,
  props: {
    includesNoData: boolean;
    includesSubQuestions: boolean;
    questions: InitiativeQuestion[];
  },
) => InitiativeQuestion[];

const getQuestionsWithAnswers = getQuestionsWithAnswersSelector as QuestionsWithAnswersSelector;

const logicKeyDefaultEnum = 'udfperc';

const frontendKeyEnums = {
  REDUCE_TIP_WASTE_GEN: 'reducetip-waste-generated',
  REDUCE_TIP_UPSTREAM_TRANSPORTDISTRIB: 'reducetip-upstream-transportdistrib',
  REDUCE_TIP_CAPITAL_GOODS: 'reducetip-capital-goods',
  REDUCE_TIP_PURCHASED_GOODS: 'reducetip-purchased-goods-and-services',
  REDUCE_TIP_BUSINESS_TRAVEL: 'reducetip-business-travel',
  REDUCE_TIP_EMPLOYEE_COMMS: 'reducetip-employeecommutes',
  REDUCE_TIP_UPSTREAM_LEASED_BUILDS: 'reducetip-upstream-leasedbuilds',
  REDUCE_TIP_DOWNSTREAM_TRANSPORTDISTRIB: 'reducetip-downstream-transportdistrib',
  REDUCE_TIP_SOLD_PRODUCT_FUELS: 'reducetip-sold-product-fuels',
  REDUCE_TIP_DOWNSTREAM_LEASED_BUILDS: 'reducetip-downstream-leasedbuilds',
  REDUCE_TIP_DOWNSTREAM_LEASED_VEH: 'reducetip-downstream-leasedvehicles',
  UPGRADE_EFFICIENT_VEHICLES: 'upgrade-efficient-vehicles',
  SWITCH_TO_SYNTH_REFRIGERANTS: 'switch-to-synth-refrigerants',
  IMPLEMENT_ENERGY_MANAGEMENT: 'implement-energy-management',
  INSTALL_AUTO_LIGHT_SWITCH: 'install-auto-light-switch',
  ADOPT_LED_LIGHTING: 'adopt-led-lighting',
  OPTIMIZE_HEATING_AND_COOLING_SYS: 'optimize-heating-and-cooling-sys',
  OFFSET_AIR_TRAVEL_SUSTAINABLE_AVFUEL: 'offset-air-travel-sustainable-avfuel',
  PETROL_TO_EV_VANS: 'petrol-to-ev-vans',
  PETROL_TO_EV_CARS: 'petrol-to-ev-cars',
  LPG_TO_EV_VANS: 'lpg-to-ev-vans',
  CNG_TO_EV_VANS: 'cng-to-ev-vans',
  HVO_TO_EV_VANS: 'hvo-to-ev-vans',
  DIESEL_TO_ELECTRICITY: 'diesel-to-electricity',
  PETROL_TO_ELECTRICITY: 'petrol-to-electricity',
  LNG_TO_ELECTRICITY: 'lng-to-electricity',
  FUELOIL_TO_ELECTRICITY: 'fueloil-to-electricity',
  BURNINGOIL_TO_ELECTRICITY: 'burningoil-to-electricity',
  NATURALGAS_TO_ELECTRICITY: 'naturalgas-to-electricity',
  TOTALVEH_DIESEL_TO_HYDROGEN: 'totalveh-diesel-to-greenhydrogen',
  TOTALVEH_CNG_TO_HYDROGEN: 'totalveh-cng-to-greenhydrogen',
  TOTALVEH_LNG_TO_HYDROGEN: 'totalveh-lng-to-greenhydrogen',
  TOTALVEH_LPG_TO_HYDROGEN: 'totalveh-lpg-to-greenhydrogen',
  TOTALVEH_HVO_TO_HYDROGEN: 'totalveh-hvo-to-greenhydrogen',
  SITE_NATURALGAS_TO_HYDROGEN: 'site-naturalgas-to-greenhydrogen',
  VEH_DIESEL_TO_HYDROGEN_VANS: 'veh-diesel-to-greenhydrogen-vans',
  VEH_DIESEL_TO_HYDROGEN_TRUCKS: 'veh-diesel-to-greenhydrogen-trucks',
  VEH_LPG_TO_HYDROGEN_VANS: 'veh-lpg-to-greenhydrogen-vans',
  VEH_LPG_TO_HYDROGEN_TRUCKS: 'veh-lpg-to-greenhydrogen-trucks',
  VEH_CNG_TO_HYDROGEN_VANS: 'veh-cng-to-greenhydrogen-vans',
  VEH_CNG_TO_HYDROGEN_TRUCKS: 'veh-cng-to-greenhydrogen-trucks',
  VEH_LNG_TO_HYDROGEN_VANS: 'veh-lng-to-greenhydrogen-vans',
  VEH_LNG_TO_HYDROGEN_TRUCKS: 'veh-lng-to-greenhydrogen-trucks',
  VEH_HVO_TO_HYDROGEN_VANS: 'veh-hvo-to-greenhydrogen-vans',
  VEH_HVO_TO_HYDROGEN_TRUCKS: 'veh-hvo-to-greenhydrogen-trucks',
};

export default function useGetInitiativeTools() {
  const { getDecoratedQuestions } = useAppContext() as { getDecoratedQuestions: () => InitiativeQuestion[] };
  const { t } = useTranslation();
  const questions = getDecoratedQuestions();
  const lastLoaded = useSelector(lastRetrievedDatestampSelector);
  const structuredData = useSelector(getApplicationStructuredDataSelector);
  // To populate with answers, we need to inject in a createdSelector
  //  we need to do it this way as we need to pass in the questions as props

  // Old - but verrry slow!
  // const selectorLoad = createSelector([
  //   (state) => getQuestionsWithAnswersSelector(state, {
  //     questions: decorateQuestions(questions).filter((q) => q.id === 'calculation'),
  //     includesNoData: true,
  //     includesSubQuestions: true,
  //   }),
  // ], (questionsWithAnswers) => questionsWithAnswers);

  /*
   Switched to memoize from proxy-memoize as createSelector was causing huge re-render issues due to being passed a callback
   But we could not move it outside of the functional hook, as we needed to use the getQuestionLookup hook (so rule of hooks)

   Using memoize appears to correctly treat 'questions' as a dynamic but immutable variable that does not get modified
   And therefore only redraw when state updates
   */
  const selectorLoad = useMemo(
    () =>
      memoize((state: RootState) =>
        getQuestionsWithAnswers(state, {
          questions: questions.filter((q) => q.id === 'calculation'),
          includesNoData: true,
          includesSubQuestions: true,
        }),
      ),
    [lastLoaded],
  );

  // Then utilise the selector (done this way as we need to run createSelector) first

  const questionsWithAnswers = useSelector(selectorLoad);
  const getAllQuestions = useCallback(
    (
      questionsFromContext: InitiativeQuestion[],
      propName: InitiativeQuestionCollectionKey = 'children',
      isWTTOnly = false,
    ): InitiativeQuestion[] => {
      const selectorPageLeafIds = new Set<string | undefined>();
      return (questionsFromContext[0].children || [])
        .flatMap((scope) => {
          return (scope[propName] || []).flatMap((question) => {
            const scopeCategory = question.scopeCategory ?? question.parent?.scopeCategory;
            if (question.subQuestions && question.subQuestions.length > 0) {
              return question.subQuestions.flatMap((subQuestion) => {
                const selectorLeafQuestions = subQuestion.subQuestions;
                if (subQuestion.isSelectorPage && selectorLeafQuestions && selectorLeafQuestions.length > 0) {
                  return selectorLeafQuestions.map((leaf) => {
                    selectorPageLeafIds.add(leaf.id);
                    const leafScopeCategory = leaf.scopeCategory ?? leaf.parent?.scopeCategory ?? scopeCategory;
                    return { ...leaf, scope: scope.id, scopeCategory: leafScopeCategory };
                  });
                }
                if (selectorPageLeafIds.has(subQuestion.id)) {
                  return [];
                }
                const subScopeCategory =
                  subQuestion.scopeCategory ?? subQuestion.parent?.scopeCategory ?? scopeCategory;
                return { ...subQuestion, scope: scope.id, scopeCategory: subScopeCategory };
              });
            }
            return { ...question, scope: scope.id, scopeCategory };
          });
        })
        .filter((question) => {
          if (question.apiKey) {
            const isWtt = question.apiKey === 'calculation-fuel-and-energy-related';
            if (isWTTOnly) {
              return isWtt;
            }

            return !isWtt;
          }
          return false;
        });
    },
    [],
  );

  const allQuestions = useMemo(() => getAllQuestions(questionsWithAnswers), [lastLoaded]);

  const getQuestionsForFilters = (
    questionsFromContext: InitiativeQuestion[],
    propName: InitiativeQuestionCollectionKey = 'children',
  ): InitiativeQuestion[] => {
    const selectorPageLeafIds = new Set<string | undefined>();
    return (questionsFromContext[0].children || [])
      .map((scope) => {
        const flattenedSubQuestions = (scope[propName] || []).flatMap((question) => {
          const scopeCategory = question.scopeCategory ?? question.parent?.scopeCategory;
          if (question.splitQuestions && question.subQuestions?.length) {
            return question.subQuestions.flatMap((subQuestion) => {
              const selectorLeafQuestions = subQuestion.subQuestions;
              if (subQuestion.isSelectorPage && selectorLeafQuestions && selectorLeafQuestions.length > 0) {
                return selectorLeafQuestions.map((leaf) => {
                  selectorPageLeafIds.add(leaf.id);
                  const leafScopeCategory = leaf.scopeCategory ?? leaf.parent?.scopeCategory ?? scopeCategory;
                  return { ...leaf, scope: scope.id, scopeCategory: leafScopeCategory };
                });
              }
              if (selectorPageLeafIds.has(subQuestion.id)) {
                return [];
              }
              const subScopeCategory = subQuestion.scopeCategory ?? subQuestion.parent?.scopeCategory ?? scopeCategory;
              return { ...subQuestion, scope: scope.id, scopeCategory: subScopeCategory };
            });
          }
          return { ...question, scope: scope.id, scopeCategory };
        });
        return flattenedSubQuestions;
      })
      .flat()
      .flat()
      .filter((question) => {
        if (question.apiKey === 'calculation-fuel-and-energy-related') {
          return false;
        }
        if (question.isSelectorPage) {
          return false;
        }
        if (!question?.apiKey && (question.subQuestions?.length ?? 0) > 0) {
          return Boolean(question.title);
        }
        if (!question?.apiKey) {
          return false;
        }
        return Boolean(question.title);
      });
  };

  const getQuestionForInitiative = (initiativeQuestionKey: string): InitiativeQuestion | undefined => {
    const mockedFormat: InitiativeQuestion[] = [{ children: questionsWithAnswers[0].children || [] }];
    const flatQuestions = getAllQuestions(mockedFormat, 'questions');
    return flatQuestions.find((question) => question?.apiKey === initiativeQuestionKey);
  };

  const scopeCategoryTonnageTracker: Array<string | undefined> = [];
  const scopeCategories = allQuestions
    .map((question) => {
      const { scopeCategory, scope, type } = question;
      const structuredRecord = structuredData.find((structuredItem) => structuredItem.key === question.apiKey);
      const hasTonnage = structuredRecord && structuredRecord.genericTonnage;

      if (type === 'question' || type === 'subQuestion') {
        if (hasTonnage) {
          scopeCategoryTonnageTracker.push(scope);
        }
        return {
          scopeCategory,
          scope,
          hasTonnage,
        };
      }
      return false;
    })
    .filter((item) => Boolean(item)) as ScopeCategoryItem[];
  const uniqueCategoryQs = uniqBy(scopeCategories, ({ scope }) => scope);

  const categoryTree = uniqueCategoryQs.map((scopeCategoryItem) => {
    const subQuestionStack: Array<{ parentId?: string; item: InitiativeQuestion }> = [];
    const emissionCategories = allQuestions
      .filter((item) => {
        return item?.scope === scopeCategoryItem?.scope;
      })
      .reduce<InitiativeQuestion[]>((baseLevel, categoryItem) => {
        if (categoryItem.type === 'subQuestion') {
          const structuredRecord = structuredData.find((structuredItem) => structuredItem.key === categoryItem.apiKey);
          const hasTonnage = structuredRecord && structuredRecord.genericTonnage;

          // For split selector pages (e.g. electricity/heat/steam), list each child as its own emission category line
          if (categoryItem.parent?.splitQuestions) {
            baseLevel.push({
              ...categoryItem,
              hasTonnage,
            });
            return baseLevel;
          }

          let hasSavedParent: InitiativeQuestion | undefined;
          if (categoryItem.depth === 5 && categoryItem.parent?.id !== 'electricity-breakdown') {
            hasSavedParent = baseLevel.find((item) => item?.id === categoryItem.parent?.parent?.id);
            if (!hasSavedParent && categoryItem.parent?.parent) {
              baseLevel.push({
                ...categoryItem.parent.parent,
                hasTonnage: null,
              });
            }
          } else {
            hasSavedParent = baseLevel.find((item) => item?.id === categoryItem.parent?.id);
            if (!hasSavedParent && categoryItem.parent) {
              baseLevel.push({
                ...categoryItem.parent,
                hasTonnage: null,
              });
            }
          }
          subQuestionStack.push({
            parentId: categoryItem.parent?.id,
            item: {
              ...categoryItem,
              hasTonnage,
            },
          });
        } else if (categoryItem.type === 'question') {
          const structuredRecord = structuredData.find((structuredItem) => structuredItem.key === categoryItem.apiKey);
          const hasTonnage = structuredRecord && structuredRecord.genericTonnage;
          baseLevel.push({
            ...categoryItem,
            hasTonnage,
          });
        }
        return baseLevel;
      }, []);

    const emissionCategoriesDecoratedSubAnswers = emissionCategories.map((item) => {
      const trackedItems = subQuestionStack
        .filter((subItem) => subItem.parentId === item.id)
        .map((subItem) => {
          return {
            ...subItem.item,
          };
        });
      if (trackedItems.length > 0) {
        const newHasTonnage = item.hasTonnage ? item.hasTonnage : trackedItems.some((subItem) => subItem.hasTonnage);
        return {
          ...item,
          children: trackedItems,
          subQuestions: trackedItems,
          hasTonnage: newHasTonnage,
        };
      }
      return {
        ...item,
      };
    });
    return {
      ...scopeCategoryItem,
      hasTonnage: scopeCategoryTonnageTracker.includes(scopeCategoryItem.scope),
      emissionCategories: emissionCategoriesDecoratedSubAnswers,
    };
  }) satisfies CategoryTreeItem[];

  return {
    questionsWithAnswers,
    getAllQuestions,
    getQuestionsForFilters,
    getFromTreeScopes: (tree: CategoryTreeItem[]) => {
      return tree.map((item) => {
        return {
          scope: item.scope,
          hasTonnage: item.hasTonnage,
        };
      });
    },
    categoryTree,
    getFromTreeCategories: (tree: CategoryTreeItem[], scope: string | null) => {
      const loader = tree
        .filter((item) => item.scope === scope)
        .map((item) => {
          return item.emissionCategories;
        });
      if (loader.length > 0) {
        return loader[0];
      }
      return [];
    },
    getLogicKey: (initiative?: Initiative | null) => {
      if (!initiative) {
        return logicKeyDefaultEnum;
      }
      return initiative?.logic_key;
    },
    getInitiativeInfo: (initiative?: Initiative | null): InitiativeInfo => {
      if (!initiative?.question_type_key) {
        return {
          categoryTitle: null,
          initiativeTitle: null,
          scopeTitle: null,
          description: null,
          background: null,
          cardBackground: null,
          logicKey: null,
        };
      }
      const question = getQuestionForInitiative(initiative.question_type_key);
      const initiativeTitle = initiative?.custom_title
        ? initiative.custom_title
        : t(`initiatives:frontendKeys.${initiative?.frontend_enum}.title`);
      const translitKeyBase = initiative?.answer_enum
        ? `questions:config.${initiative.question_type_key}.breakdown.${initiative.answer_enum}.categoryTitle`
        : `questions:config.${initiative.question_type_key}.initiativeGroupTitle`;
      const scopeTitle = question?.scope ? t(`questions:scopes.${question.scope}.title`) : null;

      let description = null;
      let background = null;
      let cardBackground = null;
      let useMask = true;
      let logicKey: string | null = null;
      if (question?.initiative) {
        logicKey = initiative.logic_key ?? null;
      }
      if (question?.initiative && typeof question?.initiative?.getVisuals === 'function') {
        const visuals = question.initiative.getVisuals({
          frontendEnum: initiative?.frontend_enum,
          questionTypeKey: initiative?.question_type_key,
          isBpOffering: initiative?.bp_offering,
        });
        if (visuals) {
          description = visuals.description;
          background = visuals.background;
          useMask = visuals.useMask;
          cardBackground = visuals.cardBackground;
        }
      }

      return {
        categoryTitle: t(`${translitKeyBase}`),
        initiativeTitle,
        scopeTitle,
        description,
        background,
        cardBackground,
        useMask,
        question,
        logicKey,
      };
    },
    getQuestionForInitiative,
    logicKeyDefaultEnum,
    frontendKeyEnums,
    validation: {
      title: {
        min: 6,
        max: 70,
      },
      description: {
        min: 6,
        max: 400,
      },
    },
  };
}
